import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { FormFooter } from "@/components/FormFooter";
import { Bell, AlertTriangle, Package, FileText, Send, Archive, Settings, Info } from "lucide-react";
import { systemConfigAPI, type NotificationConfig } from "@/lib/api";
import { normalizeNotificationConfig } from "@/lib/notifications";
import { toast } from "sonner";

export function NotificacionesSection() {
    const [config, setConfig] = useState<NotificationConfig>({
        enabledTypes: {
            stockCero: true,
            stockBajo: true,
            facturaPendiente: true,
            remitoPendiente: true,
            pedidoPendiente: true,
        },
        lowStockThreshold: 5,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const savedConfig = await systemConfigAPI.getNotificationConfig();
                if (savedConfig) {
                    setConfig(normalizeNotificationConfig(savedConfig as any));
                }
            } catch (error) {
                console.error("Failed to load notification config", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadConfig();
    }, []);

    const handleToggle = (type: keyof NotificationConfig["enabledTypes"]) => {
        setConfig(prev => ({
            ...prev,
            enabledTypes: {
                ...prev.enabledTypes,
                [type]: !prev.enabledTypes[type]
            }
        }));
    };

    const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value);
        if (!isNaN(val)) {
            setConfig(prev => ({ ...prev, lowStockThreshold: val }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await systemConfigAPI.setNotificationConfig(config);
            toast.success("Configuración de notificaciones guardada");
            // Trigger global refresh
            window.dispatchEvent(new CustomEvent('notificationsUpdated'));
        } catch (error) {
            console.error("Failed to save notification config", error);
            toast.error("Error al guardar la configuración");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center text-muted-foreground">Cargando configuración...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Bell className="h-4 w-4" />
                    <span className="text-sm font-medium">Configuración de Alertas y Notificaciones</span>
                </div>
                <FormFooter
                    onSave={() => void handleSave()}
                    saveDisabled={isSaving}
                    saving={isSaving}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Tipos de Notificación */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Bell className="h-5 w-5 text-primary" />
                            Tipos de Notificación
                        </CardTitle>
                        <CardDescription>
                            Seleccione qué eventos deben generar una notificación en el sistema.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between space-x-2">
                            <div className="flex flex-col space-y-1">
                                <Label htmlFor="stockCero" className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    Stock Crítico (Cero)
                                </Label>
                                <p className="text-xs text-muted-foreground">Notificar cuando un producto se queda sin existencias.</p>
                            </div>
                            <Switch
                                id="stockCero"
                                checked={config.enabledTypes.stockCero}
                                onCheckedChange={() => handleToggle("stockCero")}
                            />
                        </div>

                        <div className="flex items-center justify-between space-x-2">
                            <div className="flex flex-col space-y-1">
                                <Label htmlFor="stockBajo" className="flex items-center gap-2">
                                    <Package className="h-4 w-4 text-amber-500" />
                                    Stock Bajo
                                </Label>
                                <p className="text-xs text-muted-foreground">Notificar cuando el stock cae por debajo del umbral mínimo.</p>
                            </div>
                            <Switch
                                id="stockBajo"
                                checked={config.enabledTypes.stockBajo}
                                onCheckedChange={() => handleToggle("stockBajo")}
                            />
                        </div>

                        <div className="flex items-center justify-between space-x-2">
                            <div className="flex flex-col space-y-1">
                                <Label htmlFor="facturaPendiente" className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-blue-500" />
                                    Facturas Pendientes
                                </Label>
                                <p className="text-xs text-muted-foreground">Alertar sobre facturas que requieren confirmación o pago.</p>
                            </div>
                            <Switch
                                id="facturaPendiente"
                                checked={config.enabledTypes.facturaPendiente}
                                onCheckedChange={() => handleToggle("facturaPendiente")}
                            />
                        </div>

                        <div className="flex items-center justify-between space-x-2">
                            <div className="flex flex-col space-y-1">
                                <Label htmlFor="remitoPendiente" className="flex items-center gap-2">
                                    <Archive className="h-4 w-4 text-indigo-500" />
                                    Remitos Pendientes
                                </Label>
                                <p className="text-xs text-muted-foreground">Alertar sobre remitos de venta o compra sin procesar.</p>
                            </div>
                            <Switch
                                id="remitoPendiente"
                                checked={config.enabledTypes.remitoPendiente}
                                onCheckedChange={() => handleToggle("remitoPendiente")}
                            />
                        </div>

                        <div className="flex items-center justify-between space-x-2">
                            <div className="flex flex-col space-y-1">
                                <Label htmlFor="pedidoPendiente" className="flex items-center gap-2">
                                    <Send className="h-4 w-4 text-cyan-500" />
                                    Pedidos Pendientes
                                </Label>
                                <p className="text-xs text-muted-foreground">Notificar pedidos de proveedores que aún no han sido recibidos.</p>
                            </div>
                            <Switch
                                id="pedidoPendiente"
                                checked={config.enabledTypes.pedidoPendiente}
                                onCheckedChange={() => handleToggle("pedidoPendiente")}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Parámetros de Escaneo */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Settings className="h-5 w-5 text-primary" />
                            Parámetros de Escaneo
                        </CardTitle>
                        <CardDescription>
                            Configure los valores que activan las alertas automáticas.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="threshold">Umbral de Stock Bajo</Label>
                            <div className="flex items-center gap-4">
                                <NumberField
                                    id="threshold"
                                    min="0"
                                    value={config.lowStockThreshold}
                                    onChange={handleThresholdChange}
                                    className="max-w-[120px]"
                                />
                                <span className="text-sm text-muted-foreground">unidades</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Los productos con una cantidad igual o inferior a este valor aparecerán en las alertas de "Stock Bajo".
                            </p>
                        </div>

                        <div className="p-4 bg-muted/50 rounded-lg border border-border">
                            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                                <Info className="h-4 w-4 text-primary" />
                                Información de Monitoreo
                            </h4>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                El sistema realiza un escaneo automático de todos los módulos "Pendientes"
                                (Facturas, Remitos y Pedidos) en tiempo real al cargar la aplicación.
                                Los parámetros configurados aquí determinan qué información es relevante para su operación diaria.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
